import Container from '../../atoms/container/parentalPinInputContainer';
import Dial from './parentalPinDial';
import Focus from '../../../../shared/types/enums/focus';
import List from '../../sections/list/list';
import Modals from '@shared/types/enums/modals';
import useKeyboard from '../../../../shared/components/utils/hooks/useKeyboard';
import { ParentalPinInputProps as Props } from '../../../../shared/types/props/parentalPin';
import { popRoute as popRouteAction } from '../../../../shared/store/thunks/route.thunk';
import { useAppDispatch, useAppSelector } from '../../../../shared/store/hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  channelDown,
  channelUp,
} from '../../../../shared/store/thunks/channels.thunk';
import {
  clearModal,
  modalSelector,
} from '../../../../shared/store/slices/modal.slice';
import {
  disableParental,
  userSelector,
} from '../../../../shared/store/slices/user.slice';
import {
  popFocus,
  pushFocus,
} from '../../../../shared/store/slices/focus.slice';

export default function ParentalPinInput({
  allowZapping = true,
  callback,
}: Props) {
  const dispatch = useAppDispatch();
  const { current, params } = useAppSelector(modalSelector);
  const { parental } = useAppSelector(userSelector);
  const [pin, setPin] = useState(0);
  const inputPinRef = useRef(pin);
  const modalRef = useRef(current);
  const parentalPinRef = useRef(parental?.pin);
  const paramsRef = useRef(params);
  inputPinRef.current = pin;
  modalRef.current = current;
  parentalPinRef.current = parental?.pin;
  paramsRef.current = params;

  const setDial = useCallback(
    (dial: number, position: number) => {
      const units = position === 0 ? dial : pin % 10;
      const tens = position === 1 ? dial : Math.floor(pin / 10) % 10;
      const hundreds = position === 2 ? dial : Math.floor(pin / 100) % 10;
      const thousands = position === 3 ? dial : Math.floor(pin / 1000);
      return thousands * 1000 + hundreds * 100 + tens * 10 + units;
    },
    [pin]
  );

  useEffect(() => {
    dispatch(pushFocus(Focus.MODAL_INPUT));
    return () => {
      dispatch(popFocus());
    };
  }, []);

  useKeyboard({
    focus: Focus.MODAL_INPUT,
    keyDownHandler: () => null,
    keyUpHandler: (key: string) => {
      switch (key) {
        case 'BACK':
          modalRef.current === Modals.PARENTAL_PIN_ZAPPER
            ? dispatch(popRouteAction())
            : dispatch(clearModal());
          break;
        case 'CHANNEL_UP':
          allowZapping && dispatch(channelUp());
          break;
        case 'CHANNEL_DOWN':
          allowZapping && dispatch(channelDown());
          break;
        case 'SELECT':
          if (inputPinRef.current === parseInt(parentalPinRef.current || '0')) {
            dispatch(clearModal());
            dispatch(disableParental());
            if (paramsRef?.current?.callback as () => null) {
              (paramsRef?.current?.callback as () => null)();
            }
          } else {
            callback();
          }
          break;
      }
    },
  });

  return (
    <Container>
      <List focus={Focus.MODAL_INPUT}>
        <Dial
          callback={(dial: number) => setPin(() => setDial(dial, 3))}
          index={0}
        />
        <Dial
          callback={(dial: number) => setPin(() => setDial(dial, 2))}
          index={1}
        />
        <Dial
          callback={(dial: number) => setPin(() => setDial(dial, 1))}
          index={2}
        />
        <Dial
          callback={(dial: number) => setPin(() => setDial(dial, 0))}
          index={3}
        />
      </List>
    </Container>
  );
}
